import { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { appWithTranslation } from "next-i18next";
import { SessionProvider } from "next-auth/react";
import NextNprogress from "nextjs-progressbar";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { RoleBasedLayout } from "components/layout";
import debounce from "utils/debounce";

import "styles/globals.css";
import Head from "next/head";

// For Animations
import AOS from "aos";
import "aos/dist/aos.css";

import CallModalProvider from '../components/CallModalProvider';
import { FloatingWhatsApp } from 'react-floating-whatsapp';

import { useRouter } from 'next/router';
import GTM from 'react-gtm-module';

// import TawkMessenger from '../components/TawkMessenger';

const FONT_SIZE_BASE = 16;
const FONT_SIZE_RATIO = 0.04; // Ratio for responsive font size

function MyApp({ Component, pageProps }) {
  // ✅ Enable GTM
  const router = useRouter();
  const gtmId = process.env.NEXT_PUBLIC_GTM_ID || 'GTM-M4B6XB6K';
  useEffect(() => { if (!window.__gtm_initialized) { GTM.initialize({ gtmId }); window.__gtm_initialized = true; } }, []);
  useEffect(() => {
    const handleRouteChange = (url) => {
      GTM.dataLayer({ dataLayer: { event: 'pageview', page_location: window.location.href, page_path: url }, });
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => { router.events.off('routeChangeComplete', handleRouteChange); };
  }, [router.events]);

  // Dynamic font resizing based on window width
  const handleResize = useCallback(() => {
    debounce(() => {
      const fontSize =
        window.innerWidth < 425
          ? `${(window.innerWidth * FONT_SIZE_RATIO).toFixed(1)}px`
          : `${FONT_SIZE_BASE}px`;
      document.documentElement.style.fontSize = fontSize;
    }, 100);
  }, []);

  useEffect(() => {
    handleResize(); // Initial resize on component mount
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
    });
  }, []);

  // Custom layout for each page (if provided)
  const getLayout = Component.getLayout || ((page) => <RoleBasedLayout>{page}</RoleBasedLayout>);
  const waNumber = process.env.NEXT_PUBLIC_WA_NUMBER || +201019892158;
  const chatProvider = (process.env.NEXT_PUBLIC_CHAT_PROVIDER || "whatsapp").toLocaleLowerCase(); // Types: tawkto, whatsapp




  return (
    <>
      <Head>
        {/* ✅ Basic SEO */}
        <title>منصة تقنين</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta name="description" content="تقنين - الصفحة الرئيسية - استشاراتك القانونية بقت أسهل / احجز الان واحصل على استشارة قانونية فورية فى كل التخصصات مع أكبر المحامين فى مصر" />
        <meta name="keywords" content="lawyers, legal services, attorney, customer support, online consultation" />
        <meta name="author" content="Taqneen" />
        <meta name="robots" content="index, follow" />

        {/* ✅ Open Graph for Social Media (Facebook, LinkedIn, etc.) */}
        <meta property="og:title" content="تقنين" />
        <meta property="og:site_name" content="تقنين" />
        <meta property="og:description" content="تقنين - الصفحة الرئيسية - استشاراتك القانونية بقت أسهل / احجز الان واحصل على استشارة قانونية فورية فى كل التخصصات مع أكبر المحامين فى مصر" />
        <meta property="og:image" content="/tqneen/brand.svg" />
        <meta property="og:url" content="https://eg.app.tqneen.com" />
        <meta property="og:type" content="website" />

        {/* ✅ Twitter Card for Social Sharing */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="تقنين" />
        <meta name="twitter:description" content="تقنين - الصفحة الرئيسية - استشاراتك القانونية بقت أسهل / احجز الان واحصل على استشارة قانونية فورية فى كل التخصصات مع أكبر المحامين فى مصر" />
        <meta name="twitter:image" content="/tqneen/brand.svg" />

        {/* ✅ Favicons & App Icons */}
        {/* <link rel="icon" href="/favicon.ico" /> */}
        <link rel="icon" href="/tqneen/brand.svg" type="image/svg+xml" />
        <link rel="apple-touch-icon" sizes="180x180" href="/tqneen/brand.svg" />
        <link rel="manifest" href="/manifest.json" />
      </Head>
      <SessionProvider refetchOnWindowFocus={false} session={pageProps.session}>
        {/* ✅ Progress Bar */}
        <NextNprogress
          color="var(--primary)"
          startPosition={0.3}
          stopDelayMs={200}
          height={3}
          showOnShallow={true}
          showSpinner={false}
        />

        {/* ✅ Toast Notifications */}
        <ToastContainer
          position="top-center"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          style={{ zIndex: 99999 }}
        />

        <CallModalProvider>
          {/* ✅ Page Content */}
          {getLayout(<Component {...pageProps} />)}
        </CallModalProvider>

        {/* Conditional Rendering based on Environment Variable */}
        {/* ✅ WhatsApp Button */}
        {chatProvider === 'whatsapp' && (
          <div className="rtl-whatsapp">
            <FloatingWhatsApp
              phoneNumber={waNumber}
              accountName="الدعم الفني"
              avatar="https://t3.ftcdn.net/jpg/11/41/56/78/360_F_1141567842_qVTeMxBHAAOmQpvNyx1TFlxbKerymXed.jpg"
              chatMessage="👋 مرحبًا! كيف يمكننا مساعدتك؟"
              statusMessage="متصل الآن"
              placeholder="اكتب رسالتك هنا..."
              allowClickAway
              allowEsc
              notification
              notificationSound
            />
          </div>
        )}

        {/* ✅ Tawk.to Button */}
        { /* chatProvider === 'tawkto' && ( <TawkMessenger /> ) */}
      </SessionProvider>
    </>
  );
}

// ✅ PropTypes for Type Safety
MyApp.propTypes = {
  Component: PropTypes.func.isRequired,
  pageProps: PropTypes.object.isRequired,
};

export default appWithTranslation(MyApp);
